import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import type { StatementSummary } from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementList/StatementList.style'
import StatementListRow from 'src/page-components/Accounts/AccountStatementsTab/components/StatementListRow/StatementListRow'

interface StatementListProps {
  /** Shown on load, newest first — the current statement is the first row. */
  recentStatements: StatementSummary[]
  /** Revealed in place by "See more statements". */
  olderStatements: StatementSummary[]
  cardLabel: string
  /** The card whose line items each row's detail modal reads. */
  creditCardId: string | undefined
  /** True when these statements are the fabricated fallback. */
  isDemoStatements: boolean
}

/**
 * The statement history list (EX-40600).
 *
 * History expands IN PLACE — "See more statements" appends the older rows and
 * removes itself. It must not navigate to another page or open a separate
 * history view; that is a design guardrail.
 */
const StatementList: React.FC<StatementListProps> = ({
  recentStatements,
  olderStatements,
  cardLabel,
  creditCardId,
  isDemoStatements,
}) => {
  const translate = useTranslate()
  const [isShowingOlder, setIsShowingOlder] = useState(false)
  // Expanding destroys the control that was activated, so focus would otherwise
  // fall to <body> — dumping a keyboard or screen-reader user at the top of the
  // document with no signal that rows appeared below (WCAG 2.4.3). Focus moves
  // to the first revealed row instead. Only that row gets the ref.
  const firstOlderViewRef = useRef<HTMLButtonElement>(null)
  const shouldFocusFirstOlder = useRef(false)

  useEffect(() => {
    if (isShowingOlder && shouldFocusFirstOlder.current) {
      shouldFocusFirstOlder.current = false
      firstOlderViewRef.current?.focus()
    }
  }, [isShowingOlder])

  const statements = isShowingOlder
    ? [...recentStatements, ...olderStatements]
    : recentStatements

  const canShowMore = !isShowingOlder && olderStatements.length > 0
  const firstOlderIndex = recentStatements.length

  return (
    <div data-testid="statementList">
      <ul css={style.list}>
        {statements.map((statement, index) => (
          <StatementListRow
            key={statement.id}
            statement={statement}
            cardLabel={cardLabel}
            creditCardId={creditCardId}
            isDemoStatement={isDemoStatements}
            viewButtonRef={
              index === firstOlderIndex ? firstOlderViewRef : undefined
            }
          />
        ))}
      </ul>
      {canShowMore && (
        <div css={style.seeMore}>
          <Button
            variant="text"
            onClick={() => {
              shouldFocusFirstOlder.current = true
              setIsShowingOlder(true)
            }}
          >
            {translate('See more statements')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default StatementList
